import React, { useState, useEffect } from 'react';
import { ChevronRight, CheckCircle2, Clock, XCircle, User, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { directTriggerBookingStatus } from '../../lib/automations/directTrigger';
import { supabase } from '../../lib/supabase';

// Debug flag to control logging - set to false to disable logs
const enableStatusBarDebug = false;

const statuses = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface BookingStatusBarProps {
  status: string;
  createdAt: string;
  createdBy?: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  onCompletedAtChange?: (completedAt: string) => void;
  readOnly?: boolean;
  bookingId?: string;
  businessId?: string;
  canEdit?: boolean;
}

export default function BookingStatusBar({ 
  status, 
  createdAt, 
  createdBy,
  completedAt, 
  onStatusChange,
  onCompletedAtChange,
  readOnly = false,
  bookingId,
  businessId,
  canEdit = true
}: BookingStatusBarProps) {
  const [isEditingTimestamp, setIsEditingTimestamp] = useState(false);
  const [editTimestamp, setEditTimestamp] = useState('');
  const [creatorResource, setCreatorResource] = useState<any>(null);
  const [loadingCreator, setLoadingCreator] = useState(false);

  const getStatusIndex = (status: string) => {
    return statuses.findIndex(s => s.value === status);
  };

  const currentIndex = getStatusIndex(status);

  // Fetch creator resource information
  useEffect(() => {
    const fetchCreatorInfo = async () => {
      if (!createdBy || createdBy === 'admin' || createdBy === 'online') {
        setCreatorResource(null);
        return;
      }

      setLoadingCreator(true);
      try {
        // Fetch resource information from the database
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }
        
        const { data: resource, error } = await supabase
          .from('resources')
          .select('id, first_name, last_name, avatar_url')
          .eq('id', createdBy)
          .single();

        if (error) {
          console.error('Error fetching creator resource:', error);
          setCreatorResource(null);
        } else {
          setCreatorResource(resource);
        }
      } catch (error) {
        console.error('Error fetching creator resource:', error);
        setCreatorResource(null);
      } finally {
        setLoadingCreator(false);
      }
    };

    fetchCreatorInfo();
  }, [createdBy]);

  // Render creator information
  const renderCreatorInfo = () => {
    if (!createdBy) return null;

    if (createdBy === 'admin') {
      return (
        <div className="flex items-center space-x-1 ml-2">
          <User className="h-3 w-3 text-purple-500" />
          <span className="text-xs text-purple-600 font-medium">Admin</span>
        </div>
      );
    }

    if (createdBy === 'online') {
      return (
        <div className="flex items-center space-x-1 ml-2">
          <Globe className="h-3 w-3 text-blue-500" />
          <span className="text-xs text-blue-600 font-medium">Online</span>
        </div>
      );
    }

    if (loadingCreator) {
      return (
        <div className="flex items-center space-x-1 ml-2">
          <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
      );
    }

    if (creatorResource) {
      return (
        <div className="flex items-center space-x-1 ml-2">
          {creatorResource.avatar_url ? (
            <img 
              src={creatorResource.avatar_url} 
              alt={`${creatorResource.first_name} ${creatorResource.last_name}`}
              className="h-3 w-3 sm:h-4 sm:w-4 rounded-full object-cover"
            />
          ) : (
            <div className="h-3 w-3 sm:h-4 sm:w-4 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-[8px] sm:text-[10px] font-bold">
                {creatorResource.first_name?.[0]}{creatorResource.last_name?.[0]}
              </span>
            </div>
          )}
          <span className="text-xs text-gray-600 font-medium">
            {creatorResource.first_name} {creatorResource.last_name}
          </span>
        </div>
      );
    }

    return null;
  };
  
  // Handle status change and directly trigger automations
  const handleStatusChange = async (newStatus: string) => {
    // Only certain statuses should trigger automations
    const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
    
    if (enableStatusBarDebug) {
      console.log(`[STATUS BAR] 🔍 Status change: ${status} → ${newStatus}`);
      console.log(`[STATUS BAR] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
      console.log(`[STATUS BAR] 🔍 Booking ID available: ${!!bookingId}`);
      console.log(`[STATUS BAR] 🔍 Business ID available: ${!!businessId}`);
    }
    
    // First call the parent's onStatusChange to update the database
    if (onStatusChange) {
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableStatusBarDebug) {
          console.log(`[STATUS BAR] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[STATUS BAR] 📋 Booking ID: ${bookingId}`);
          console.log(`[STATUS BAR] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableStatusBarDebug) {
              console.log(`[STATUS BAR] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[STATUS BAR] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[STATUS BAR] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[STATUS BAR] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[STATUS BAR] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[STATUS BAR] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  // Format timestamp for datetime-local input
  const formatTimestampForInput = (timestamp: string) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Handle starting timestamp edit
  const handleEditTimestamp = () => {
    if (completedAt && status === 'completed' && !readOnly && canEdit && onCompletedAtChange) {
      setEditTimestamp(formatTimestampForInput(completedAt));
      setIsEditingTimestamp(true);
    }
  };

  // Handle saving timestamp changes
  const handleSaveTimestamp = () => {
    if (editTimestamp && onCompletedAtChange) {
      const newCompletedAt = new Date(editTimestamp).toISOString();
      onCompletedAtChange(newCompletedAt);
      setIsEditingTimestamp(false);
      setEditTimestamp('');
    }
  };

  // Handle canceling timestamp edit
  const handleCancelTimestamp = () => {
    setIsEditingTimestamp(false);
    setEditTimestamp('');
  };

  // Don't show the status bar if the booking is cancelled
  if (status === 'cancelled') {
    return (
      <div className="bg-[#2f2f2f] text-gray-400 border-b border-[#3f3f3f] overflow-hidden">
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-center justify-between py-1.5 sm:py-2 px-3 sm:px-6">
            <div className="flex items-center space-x-2">
              <XCircle className="h-4 w-4 text-red-400" />
              <span className="text-red-400 text-sm">Booking Cancelled</span>
            </div>
            <div className="text-xs">
              {completedAt || createdAt ? (
                <>Cancelled on {format(new Date(completedAt || createdAt), 'MMM d, yyyy')}</>
              ) : (
                'Cancelled'
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
      <div className="max-w-5xl mx-auto relative">
        <div className="flex items-center justify-between py-3 px-3 sm:px-6 min-w-[500px]">
          <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
            {statuses.map((s, index) => (
              <React.Fragment key={s.value}>
                <button
                  onClick={() => {
                    if (!readOnly && canEdit && index <= currentIndex + 1) {
                      handleStatusChange(s.value);
                    }
                  }}
                  disabled={readOnly || !canEdit || index > currentIndex + 1}
                  className={`font-bold transition-colors px-3 py-2 rounded-lg ${
                    index <= currentIndex
                      ? status === 'scheduled' && index === 0
                        ? 'text-blue-700 bg-blue-50 shadow-sm'
                        : status === 'in_progress' && index <= 1
                        ? 'text-yellow-700 bg-yellow-50 shadow-sm'
                        : status === 'completed' && index <= 2
                        ? 'text-green-700 bg-green-50 shadow-sm'
                        : 'text-blue-700 bg-blue-50 shadow-sm'
                      : index === currentIndex + 1 && !readOnly && canEdit
                      ? 'text-gray-500 hover:text-blue-700 hover:bg-blue-50'
                      : 'text-gray-400'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  {s.label}
                </button>
                {index < statuses.length - 1 && (
                  <ChevronRight className={`h-3 w-3 sm:h-4 sm:w-4 ${
                    index < currentIndex ? 'text-gray-600' : 'text-gray-300'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm whitespace-nowrap ml-4">
            {status === 'completed' ? (
              <>
                <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                {isEditingTimestamp ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="datetime-local"
                      value={editTimestamp}
                      onChange={(e) => setEditTimestamp(e.target.value)}
                      className="text-xs px-2 py-1 rounded-lg border border-gray-300 bg-white/80 text-gray-900 focus:border-gray-500 focus:ring-1 focus:ring-gray-500"
                    />
                    <button
                      onClick={handleSaveTimestamp}
                      className="px-2 py-1 text-xs bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-bold"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancelTimestamp}
                      className="px-2 py-1 text-xs bg-gray-400 text-white rounded-lg hover:bg-gray-500 font-bold"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span 
                      onClick={handleEditTimestamp}
                      className={`text-gray-600 font-medium ${!readOnly && canEdit && onCompletedAtChange ? 'cursor-pointer hover:text-gray-800 hover:underline' : ''}`}
                      title={!readOnly && canEdit && onCompletedAtChange ? 'Click to edit completion time' : ''}
                    >
                      Completed on {completedAt && !isNaN(new Date(completedAt).getTime()) ? format(new Date(completedAt), 'MMM d, yyyy') : 'Unknown'}
                    </span>
                    {renderCreatorInfo()}
                  </div>
                )}
              </>
            ) : (
              <>
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                <div className="flex items-center">
                  <span className="text-gray-600 font-medium">Created on {createdAt && !isNaN(new Date(createdAt).getTime()) ? format(new Date(createdAt), 'MMM d, yyyy') : 'Unknown'}</span>
                  {renderCreatorInfo()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}